import React, { useEffect, useState, useMemo } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import SectionCard from "./ui/SectionCard";
import { diffInDays, formatDateId, getTodayId } from "../utils/date"; 
import {
  getRecoverySymptomsForDay,
  saveRecoverySymptomsCheckin,
} from "../lib/recoveryDailyLogs";
import {
  DailySymptoms,
  SymptomKey,
  SymptomRiskLevel,
} from "../types/recovery"; 
import { symptomConfigs } from "../config/symptomsConfig";
import { AnimatePresence, motion } from "framer-motion";
import { BirthType } from "../hooks/useLessons";

// Internal interfaces to resolve 'any' and config access
interface SymptomOption {
  label: string;
  value: string;
  risk?: SymptomRiskLevel;
}

interface SymptomConfig {
  label: string;
  question: string;
  options: SymptomOption[];
}

const ALL_SYMPTOM_KEYS: SymptomKey[] = [
  "perinealPain",
  "cSectionWound",
  "fatigue",
  "breastfeedingPain",
  "bladderControl",
  "bowelFunction",
  "pelvicHeaviness",
  "painAndMovement",
];

const WEEK_SYMPTOMS: Record<number, SymptomKey[]> = {
  1: ["perinealPain", "cSectionWound", "fatigue"],
  2: ["breastfeedingPain", "fatigue", "pelvicHeaviness"],
  3: ["painAndMovement", "fatigue", "pelvicHeaviness"],
  4: ["bladderControl", "bowelFunction", "fatigue", "pelvicHeaviness", "painAndMovement"],
  5: ["fatigue", "pelvicHeaviness", "painAndMovement"],
  6: ALL_SYMPTOM_KEYS,
};

const clampWeek = (w: number): number => Math.min(Math.max(w, 1), 6);

const severityClassMap: Record<string, string> = {
  none: "bg-[#f5f5f5] text-[#121212] border-transparent",
  mild: "bg-[#dce7c9] text-[#121212] border-transparent",
  moderate: "bg-[#f6dfc7] text-[#121212] border-transparent",
  severe: "bg-[#f4b7a8] text-[#121212] border-transparent",
};

const chipBase =
  "inline-flex items-center justify-center border px-3 py-[7px] rounded-full text-[14px] leading-[18px] transition active:scale-[0.98]";

type RecoverySymptomsCardProps = {
  weekNumberOverride?: number;
  dateIdOverride?: string;
  onSaveSuccess?: () => void;
  birthType?: BirthType;
};

const RecoverySymptomsCard: React.FC<RecoverySymptomsCardProps> = ({
  weekNumberOverride,
  dateIdOverride,
  onSaveSuccess,
  birthType,
}) => {
  const { user } = useAuth();
  const todayId = dateIdOverride ?? getTodayId();

  const [symptoms, setSymptoms] = useState<DailySymptoms>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState(false);
  const [programmeStartId, setProgrammeStartId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const ref = doc(db, "users", user.uid, "recoveryProgrammes", "physical_wellbeing_6w");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as { startDateId?: string };
          if (data.startDateId) setProgrammeStartId(data.startDateId);
        }
      },
      (err) => {
        console.error("Error loading programme:", err);
      }
    );

    return () => unsub();
  }, [user]);

  const effectiveWeekNumber: number = useMemo(() => {
    if (weekNumberOverride != null) return clampWeek(weekNumberOverride);
    if (programmeStartId) {
      const diff = diffInDays(programmeStartId, formatDateId(new Date()));
      return clampWeek(diff >= 0 ? Math.floor(diff / 7) + 1 : 1);
    }
    return 1;
  }, [weekNumberOverride, programmeStartId]);

  const weekSymptoms: SymptomKey[] = useMemo(() => {
    const baseKeys = WEEK_SYMPTOMS[effectiveWeekNumber] ?? ALL_SYMPTOM_KEYS;
    return baseKeys.filter(key => {
      if (key === "cSectionWound" && birthType !== "csection") return false;
      if (key === "perinealPain" && birthType === "csection") return false;
      return true;
    });
  }, [effectiveWeekNumber, birthType]);

  useEffect(() => {
    const load = async () => {
      if (!user) { setLoading(false); return; }
      setLoading(true);
      try {
        const symptomsData = await getRecoverySymptomsForDay(user.uid, todayId);
        setSymptoms(symptomsData);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, todayId]);

  const handleSelectLevel = (key: SymptomKey, value: string | null) => {
    setSymptoms((prev) => {
      const next: DailySymptoms = { ...prev };
      if (value === null) {
        delete next[key];
        return next;
      }
      const config = (symptomConfigs as Record<string, SymptomConfig>)[key];
      const risk = config?.options?.find((o) => o.value === value)?.risk;
      
      (next as Record<string, unknown>)[key] = { level: value, risk };
      return next;
    });
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const alertSymptoms: SymptomKey[] = (Object.keys(symptoms) as SymptomKey[]).filter(key => {
        const symptom = symptoms[key] as { risk?: SymptomRiskLevel } | undefined;
        return symptom?.risk === "alert";
      });

      const alertSummary = {
        hasAnyAlert: alertSymptoms.length > 0,
        alertSymptoms,
        hasData: Object.keys(symptoms).length > 0
      };

      await saveRecoverySymptomsCheckin(user.uid, todayId, effectiveWeekNumber, symptoms, alertSummary);
      setSaved(true);
      if (onSaveSuccess) setTimeout(onSaveSuccess, 600);
    } catch (err) {
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = weekSymptoms.every((key) => key in symptoms);

  return (
    <section className="space-y-6">
      {/* BRANDED FULL SCREEN LOADER (Used for both initial load and saving) */}
      {(loading || saving) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100%',
          backgroundColor: '#fcf9f7',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2147483647,
          pointerEvents: 'all'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(30, 94, 91, 0.1)',
            borderTop: '4px solid #1e5e5b',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            marginBottom: '20px',
            boxSizing: 'border-box'
          }} />
          <div style={{ fontFamily: 'DM Sans, sans-serif', textAlign: 'center' }}>
            <p style={{
              color: '#0c2624',
              fontSize: '18px',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              margin: 0,
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              {saving ? "Saving Check-in" : "BodyOwn"}
            </p>
          </div>
          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
            body { overflow: hidden !important; }
          `}</style>
        </div>
      )}

      {!loading && weekSymptoms.map((key) => {
        const config = (symptomConfigs as Record<string, SymptomConfig>)[key];
        if (!config) return null;
        
        const current = symptoms[key] as { level?: string; risk?: SymptomRiskLevel } | undefined;
        
        return (
          <SectionCard 
            key={key} 
            title={config.label ?? ""} 
            className="relative"
          >
            {current && (
              <button 
                type="button" 
                onClick={() => handleSelectLevel(key, null)} 
                className="absolute top-1 right-6 z-10 text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-[#1e5e5b] transition-colors"
              >
                Reset
              </button>
            )}

            <p className="text-xs text-gray-500 mb-4 pr-36 leading-relaxed">
              {config.question}
            </p>

            {current?.risk === "alert" && (
              <div className="mb-3">
                <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700 uppercase">
                  Needs attention
                </span>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {config.options?.map((opt) => {
                const isActive = current?.level === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelectLevel(key, opt.value)}
                    className={`${severityClassMap[opt.value] ?? ""} ${chipBase} ${
                      isActive ? "ring-2 ring-black/10 border-black/20" : "opacity-80"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </SectionCard>
        );
      })}

      <div className="mt-8">
        <button
          type="button"
          onClick={handleSave}
          disabled={!user || loading || saving || !canSubmit || saved}
          className={`w-full h-12 rounded-xl font-bold text-base transition-all duration-200 ${
            saved ? "bg-green-600 text-white" : "bg-[#1e5e5b] text-white"
          } disabled:opacity-40 disabled:grayscale`}
        >
          <AnimatePresence mode="wait" initial={false}>
            {saved ? (
              <motion.span key="saved" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>Logged Successfully</motion.span>
            ) : saving ? (
              <motion.span key="saving" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>Saving...</motion.span>
            ) : (
              <motion.span key="idle">Complete Check-in</motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </section>
  );
};

export default RecoverySymptomsCard;